import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from './useDebounce';

export interface AsyncOption {
  value: string;
  label: string;
}

export interface UseAsyncOptionsConfig {
  /**
   * Function that fetches options from API based on search query
   * @param searchQuery - The search query string
   * @returns Promise resolving to array of options
   */
  fetchOptions: (searchQuery: string) => Promise<AsyncOption[]>;
  /**
   * Debounce delay in milliseconds (default: 500)
   */
  debounceDelay?: number;
  /**
   * Minimum characters before triggering search (default: 0)
   */
  minSearchLength?: number;
  /**
   * Initial options to show before user starts typing
   */
  initialOptions?: AsyncOption[];
  /**
   * Whether to fetch options on mount (default: false)
   */
  fetchOnMount?: boolean;
}

/**
 * Custom hook for managing async dropdown options with debounced search
 */
export function useAsyncOptions(config: UseAsyncOptionsConfig) {
  const {
    fetchOptions,
    debounceDelay = 500,
    minSearchLength = 0,
    initialOptions = [],
    fetchOnMount = false,
  } = config;

  const [options, setOptions] = useState<AsyncOption[]>(initialOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to store fetchOptions to avoid recreating loadOptions
  const fetchOptionsRef = useRef(fetchOptions);
  const minSearchLengthRef = useRef(minSearchLength);
  const initialOptionsRef = useRef(initialOptions);

  // Update refs when they change
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
    minSearchLengthRef.current = minSearchLength;
    initialOptionsRef.current = initialOptions;
  }, [fetchOptions, minSearchLength, initialOptions]);

  const debouncedSearchQuery = useDebounce(searchQuery, debounceDelay);

  // Track the last query we fetched to avoid duplicate calls
  const lastFetchedQueryRef = useRef<string | null>(null);

  const loadOptions = useCallback(async (query: string) => {
    // Skip if we already fetched this exact query
    if (lastFetchedQueryRef.current === query) {
      return;
    }

    // Don't fetch if query is too short
    if (query.length < minSearchLengthRef.current) {
      if (query.length === 0 && initialOptionsRef.current.length > 0) {
        setOptions(initialOptionsRef.current);
      } else {
        setOptions([]);
      }
      setIsLoading(false);
      lastFetchedQueryRef.current = query;
      return;
    }

    // Mark that we're fetching this query
    lastFetchedQueryRef.current = query;
    setIsLoading(true);
    setError(null);

    try {
      const fetchedOptions = await fetchOptionsRef.current(query);
      // Only update if this is still the current query (handle race conditions)
      if (lastFetchedQueryRef.current === query) {
        setOptions(fetchedOptions);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch options';
      setError(errorMessage);
      if (lastFetchedQueryRef.current === query) {
        setOptions([]);
      }
      console.error('Error fetching async options:', err);
    } finally {
      if (lastFetchedQueryRef.current === query) {
        setIsLoading(false);
      }
    }
  }, []); // Empty deps - we use refs for all dependencies

  // Track if this is the first render
  const isFirstRenderRef = useRef(true);

  // Fetch options when debounced search query changes
  useEffect(() => {
    // Skip on first render unless fetchOnMount is explicitly true
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      if (fetchOnMount && debouncedSearchQuery === '') {
        loadOptions('');
      }
      return;
    }

    // Only fetch if user has typed something
    if (searchQuery !== '' && debouncedSearchQuery !== '') {
      loadOptions(debouncedSearchQuery);
    } else if (searchQuery === '' && debouncedSearchQuery === '') {
      // Only reset to initial options if we're not currently loading
      // AND we're not in the middle of a fetch (lastFetchedQueryRef is null)
      // This prevents clearing options while API call is in progress or right after it completes
      if (!isLoading && lastFetchedQueryRef.current === null) {
        if (initialOptionsRef.current.length > 0) {
          setOptions(initialOptionsRef.current);
        } else {
          setOptions([]);
        }
      }
    }
  }, [debouncedSearchQuery, loadOptions, searchQuery, fetchOnMount, isLoading]);

  // Reset last fetched query when search query is cleared (but only if not loading and not currently fetching)
  useEffect(() => {
    // Only reset if search is cleared AND we're not loading AND we're not in the middle of a fetch
    if (searchQuery === '' && !isLoading && lastFetchedQueryRef.current === null) {
      if (initialOptionsRef.current.length > 0) {
        setOptions(initialOptionsRef.current);
      } else {
        setOptions([]);
      }
    }
  }, [searchQuery, isLoading]);

  // Update options when initialOptions change (when new data is loaded) and search is empty
  useEffect(() => {
    if (searchQuery === '' && initialOptionsRef.current.length > 0) {
      setOptions(initialOptionsRef.current);
    }
  }, [initialOptions, searchQuery]);

  return {
    options,
    searchQuery,
    setSearchQuery,
    isLoading,
    error,
    refresh: () => {
      lastFetchedQueryRef.current = null;
      loadOptions(debouncedSearchQuery);
    },
  };
}
